import React, { Fragment, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import DialogBoxComponent from "../../../components/DialogBoxComponent";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Stack, Typography } from "@mui/material";
import {
  getImportControlList,
  importNewControl,
  updateIControl,
} from "./helper";
import { useUserContext } from "../../../context/auth/UserContext";

export const ControlImport = (props: {
  openDialog: boolean;
  handleCancel: () => void;
  refreshList: () => void;
  controlList: any[];
}) => {
  const { userId, companyProfileID } = useUserContext();
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [importControlList, setImportControlList] = useState<any[]>([]);
  const [importControlListOrg, setImportControlListOrg] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [existingControl, setExistingControl] = useState<any[]>([]);
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openImportConfirmation: false,
    openSuccessDialogBox: false,
  });

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectAll(false);
      setChecked([]);
    } else {
      setSelectAll(true);
      const newChecked = importControlList.map((ele) => ele.controlsImportID);
      setChecked(newChecked);
    }
  };

  const handleClose = () => {
    if (props.handleCancel) {
      props.handleCancel();
    }
    setChecked([]);
    setSelectAll(false);
    setExistingControl([]);
    setImportControlList([]);
    setImportControlListOrg([]);
    setOpen(false);
  };

  const handleCloseDialog = () => {
    if (dialogState.title !== "Error") {
      setDialogState({
        ...dialogState,
        openImportConfirmation: false,
        openSuccessDialogBox: false,
      });
      props.refreshList();
      handleClose();
    } else {
      setDialogState({
        ...dialogState,
        openImportConfirmation: false,
        openSuccessDialogBox: false,
      });
    }
  };

  const getSelectedControl = () => {
    return importControlList.filter((ele) =>
      checked.includes(ele.controlsImportID)
    );
  };

  const getExistingControl = (selectedControl: any[]) => {
    // console.log("getExistingControl", props.controlList);

    return selectedControl
      .filter((control: any) => {
        return props.controlList.some(
          (ele: any) => ele.controlsName === control.controlsName
        );
      })
      .map((control: any) => {
        const matchedControl: any = props.controlList.find(
          (ele: any) => ele.controlsName === control.controlsName
        );
        return {
          ...control,
          controlsID: matchedControl.controlsID,
        };
      });
  };

  const handleImport = async () => {
    const selectedControl = getSelectedControl();
    const isExsistingControl = await getExistingControl(selectedControl);
    setExistingControl(isExsistingControl);
    if (isExsistingControl.length > 0) {
      const existingControlNames = isExsistingControl
        .map((control) => control.controlsName)
        .join(", ");
      setDialogState({
        title: "Confirm",
        body: `The following control are already existing in the list: <strong>${existingControlNames}</strong>. If you import, they will be overwritten. Are you sure?`,
        action: "Import",
        actionCancel: "Cancel",
        openImportConfirmation: true,
        openSuccessDialogBox: false,
      });
    } else {
      importControl();
    }
  };

  const importControl = async () => {
    setIsLoading(true);
    const selectedControl = getSelectedControl();
    const newlyImportControl = selectedControl.filter(
      (control) =>
        !existingControl.some(
          (existingControl) =>
            existingControl.controlsName === control.controlsName
        )
    );

    let hasError = false;
    const date = new Date();

    //Import new Control
    if (newlyImportControl.length > 0) {
      const payload = newlyImportControl.map((ele) => {
        return {
          controlsName: ele.controlsName,
          controlsTypeId: ele.controlsTypeId,
          createDate: date,
          createdBy: userId,
          companyProfileID: companyProfileID,
          templateId: ele.controlsImportID,
        };
      });
      //   console.log("payload newlyImportControl", payload);
      const resImport = await importNewControl(payload);
      if (!resImport.succeeded) {
        hasError = true;
      }
    }

    //Update existing control
    if (existingControl.length > 0) {
      for (const ele of existingControl) {
        const payload = {
          controlsName: ele.controlsName,
          controlsTypeId: ele.controlsTypeId,
          updateDate: date,
          updatedBy: userId,
          controlsID: ele.controlsID,
          companyProfileID: companyProfileID,
        };

        try {
          const resUpdate = await updateIControl(payload);
          if (!resUpdate.succeeded) {
            hasError = true;
            break;
          }
        } catch (error) {
          console.error("@Error", error);
          hasError = true;
          break;
        }
      }
    }

    if (hasError) {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openImportConfirmation: false,
        openSuccessDialogBox: true,
      });
    } else {
      setDialogState({
        title: "Success",
        body: "Control successfully imported",
        action: "OK",
        actionCancel: "",
        openImportConfirmation: false,
        openSuccessDialogBox: true,
      });
    }
    setIsLoading(false);
  };

  const fetchControlImport = async () => {
    try {
      const res = await getImportControlList();
      const filteredRes = res.filter((resItem: any) => {
        return !props.controlList.some(
          (existingItem: any) =>
            existingItem.controlsName === resItem.controlsName &&
            existingItem.controlsTypeId == resItem.controlsTypeId
        );
      });
      setImportControlList(filteredRes);
      setImportControlListOrg(res);

      // console.log("props", res, props.controlList, filteredRes);
    } catch (error) {
      console.log("@@Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setOpen(props.openDialog);
    setIsLoading(true);
    if (props.openDialog) {
      fetchControlImport();
    }
  }, [props.openDialog]);

  return (
    <Fragment>
      <Dialog open={open} onClose={handleClose} keepMounted>
        <DialogTitle sx={{ fontSize: "1.1rem" }}>Import Control</DialogTitle>
        <DialogContent>
          {importControlList.length > 0 ? (
            <Stack direction="row" sx={{ mx: 2 }}>
              <Checkbox
                size="small"
                edge="start"
                checked={selectAll}
                onChange={handleSelectAll}
                tabIndex={-1}
                disableRipple
                sx={{ p: 0, mr: 3 }}
              />
              <span>Select All</span>
            </Stack>
          ) : null}
          <DialogContentText sx={{ height: "300px", overflowY: "scroll" }}>
            <List sx={{ width: "300px", bgcolor: "background.paper" }}>
              {!isLoading ? (
                importControlListOrg.length > 0 ? (
                  importControlList.length > 0 ? (
                    importControlList
                      .sort((a, b) => {
                        const nameA = a.controlsName || "";
                        const nameB = b.controlsName || "";
                        return nameA.localeCompare(nameB);
                      })
                      .map((ele) => {
                        const labelId = `checkbox-list-label-${ele}`;

                        return (
                          <ListItem
                            key={ele.controlsImportID}
                            disablePadding
                            disableGutters
                          >
                            <ListItemButton
                              role={undefined}
                              onClick={handleToggle(ele.controlsImportID)}
                              dense
                            >
                              <ListItemIcon sx={{ minWidth: "2rem" }}>
                                <Checkbox
                                  size="small"
                                  edge="start"
                                  checked={
                                    checked.indexOf(ele.controlsImportID) !== -1
                                  }
                                  tabIndex={-1}
                                  disableRipple
                                  inputProps={{ "aria-labelledby": labelId }}
                                  sx={{ p: 0 }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                id={labelId}
                                primary={ele.controlsName}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })
                  ) : (
                    <Typography textAlign="center">
                      All the controls are already imported.
                    </Typography>
                  )
                ) : (
                  <Typography textAlign="center">
                    No controls to import
                  </Typography>
                )
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center">
                  <CircularProgress />
                </Box>
              )}
            </List>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "0.5rem 1.5rem" }}>
          <Button
            size="small"
            variant="contained"
            className="table-btn"
            disabled={isLoading}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            className="table-btn"
            disabled={checked.length === 0 || isLoading}
            onClick={handleImport}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
      {
        //Confirmation
        <DialogBoxComponent
          openDialogBox={dialogState.openImportConfirmation}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={importControl}
          handleCancel={handleCloseDialog}
        />
      }
      {
        //success
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleCloseDialog}
        />
      }
    </Fragment>
  );
};
